/**
 * Phase 19 (DEC-325) — the Operations | Tanks | Management switch at the top of the
 * dashboards. Operations and Tanks show for OPS_DASHBOARD_ROLES (the ASSIGN_JOB holders,
 * which gate both API routes); Management only for MGMT_DASHBOARD_ROLES (D-2).
 * Phase 13 / 13.3 (DEC-351) added Tanks. It is the TANKS_TAB feature (§58 PR 6a, DEC-366): on from
 * PROFESSIONAL up, or by a Paelode override. Shown only once the feature is KNOWN to be on.
 */

import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserProfile } from '../../lib/use-user-timezone';
import { useFeatureEnabled } from '../../lib/use-features';
import { MGMT_DASHBOARD_ROLES, OPS_DASHBOARD_ROLES } from '../../lib/role-gates';

export const TANKS_TAB_PATH = '/dashboard/tanks';

const TABS = [
  { path: '/dashboard', label: 'Operations', roles: OPS_DASHBOARD_ROLES },
  { path: TANKS_TAB_PATH, label: 'Tanks', roles: OPS_DASHBOARD_ROLES },
  { path: '/dashboard/management', label: 'Management', roles: MGMT_DASHBOARD_ROLES },
] as const;

export function DashboardTabs(): React.JSX.Element | null {
  const { data } = useUserProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const role = data?.profile.role ?? '';
  // Only when known to be on: a tab that 403s on click is worse than one that appears a moment late.
  const tanksTab = useFeatureEnabled('TANKS_TAB') === true;
  const visible = TABS.filter((t) => t.roles.has(role) && (t.path !== TANKS_TAB_PATH || tanksTab));
  // A lone tab is not a switch.
  if (visible.length < 2) return null;
  return (
    <div role="tablist" aria-label="Dashboards" style={{ display: 'flex', gap: '4px' }}>
      {visible.map((t) => {
        const selected = location.pathname === t.path;
        return (
          <button
            key={t.path}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={(): void => void navigate(t.path)}
            style={{
              minHeight: '36px',
              padding: '0 14px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-primary)',
              background: selected ? 'var(--color-accent-blue)' : 'var(--color-bg-tertiary)',
              color: selected ? 'var(--color-on-accent)' : 'var(--color-text-primary)',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
